import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent, DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const MODAL_ID = 'treatment-param-selector-modal';
const FORM_SELECTOR = '[data-treatment-param-form]';
const CLOSE_FALLBACK_MS = 400;
// Sent by neer-moment-buy.liquid when "Add to bag" is pressed; detail is { id, quantity, price }.
const ADD_REQUEST_EVENT = 'treatment-param:add';
// The buy card's accent pair and where it goes on the window (contracts/style-shade-window.md, W10).
const ACCENT_PROPERTIES = [
  ['--nmbuy-accent', '--tps-cta'],
  ['--nmbuy-accent-text', '--tps-cta-text'],
];
// Opened from the buy card, the window takes that card's size (research D16, D20): the gap a whole card needs to the
// screen edge, the smallest a style photo may shrink to so both steps fit, and the variables the stylesheet reads.
const FIT_MARGIN = 16;
const MIN_PHOTO_HEIGHT = 64;
const FIT_PROPERTIES = ['--tps-fit-top', '--tps-fit-left', '--tps-fit-width', '--tps-fit-height', '--tps-lift-from', '--tps-photo-height'];
// With no buy card on screen the window takes the unseen copy's box (research D21) and rises this far, as tps-rise does.
const SIZER_RISE = 16;

/** The buy card the window covers while adding, else the snippet's unseen copy of one (quick add, a bag fix). */
let fitCard = null;
/** True when fitCard is that unseen copy, which sits nowhere the shopper can see. */
let fitFromSizer = false;

/**
 * The Moment waiting to be added on Save. Null when the dialog is editing an existing cart line.
 * @type {{ id: number, quantity: number } | null}
 */
let pendingAdd = null;

/** Each summary's Liquid-rendered price, shown when an add event carries none. */
const liquidPrices = new WeakMap();

/**
 * Shows an error message inside the modal.
 * @param {HTMLElement | null} errorElement
 * @param {string} message
 */
function setError(errorElement, message) {
  if (!errorElement) return;
  errorElement.hidden = message.length === 0;
  errorElement.textContent = message;
}

/**
 * Shows or clears one step's "choose one" hint; while shown, its fieldset is invalid and described by it.
 * @param {HTMLElement} modalComponent
 * @param {'designStyle' | 'shade'} group - The step's radio name.
 * @param {boolean} missing
 */
function markMissing(modalComponent, group, missing) {
  const hint = modalComponent.querySelector(`[data-treatment-param-missing="${group}"]`);
  if (!(hint instanceof HTMLElement)) return;
  hint.hidden = !missing;

  const fieldset = hint.closest('fieldset');
  if (!fieldset) return;
  if (missing) {
    fieldset.setAttribute('aria-invalid', 'true');
    if (hint.id) fieldset.setAttribute('aria-describedby', hint.id);
  } else {
    fieldset.removeAttribute('aria-invalid');
    fieldset.removeAttribute('aria-describedby');
  }
}

/**
 * Gives the main button the buy card's accent pair, or drops it so the stylesheet's coral fallback applies.
 * @param {HTMLElement} dialog
 * @param {Element | null} buyCard
 */
function copyAccent(dialog, buyCard) {
  const style = buyCard ? getComputedStyle(buyCard) : null;
  for (const [from, to] of ACCENT_PROPERTIES) {
    const value = style?.getPropertyValue(from).trim();
    if (value) dialog.style.setProperty(to, value);
    else dialog.style.removeProperty(to);
  }
}

/**
 * A card's on-screen box, or null when there is none to cover (no card, or no layout yet).
 * @param {Element | null} card
 */
function cardBox(card) {
  const box = card?.getBoundingClientRect();
  return box && box.width > 0 && box.height > 0 ? box : null;
}

/**
 * Sizes the open window to the buy card. When the card fits the screen the window takes its size and left edge, rests
 * centred down the screen, and lifts from the card's own spot (--tps-lift-from), so the page never scrolls (research
 * D20). Else (phones) it takes only the width and left edge. The style photos then shrink just enough for both steps to
 * fit. Sized from the unseen copy, it rises from just below instead. With no box at all it stays centred.
 * @param {HTMLElement} dialog
 * @param {Element | null} card
 * @param {boolean} [fromSizer]
 */
function fitToCard(dialog, card, fromSizer = false) {
  for (const name of FIT_PROPERTIES) dialog.style.removeProperty(name);
  const box = cardBox(card);
  if (!box) {
    delete dialog.dataset.fit;
    return;
  }

  const whole = box.height <= window.innerHeight - 2 * FIT_MARGIN;
  dialog.dataset.fit = whole ? 'card' : 'width';
  dialog.style.setProperty('--tps-fit-left', `${box.left}px`);
  dialog.style.setProperty('--tps-fit-width', `${box.width}px`);
  if (!whole) return;
  const top = Math.round((window.innerHeight - box.height) / 2);
  dialog.style.setProperty('--tps-fit-top', `${top}px`);
  dialog.style.setProperty('--tps-fit-height', `${box.height}px`);
  dialog.style.setProperty('--tps-lift-from', `translateY(${fromSizer ? SIZER_RISE : box.top - top}px)`);

  const body = dialog.querySelector('[data-treatment-param-body]');
  const photo = dialog.querySelector('[data-treatment-param-photo]');
  const overflow = body ? body.scrollHeight - body.clientHeight : 0;
  if (overflow > 0 && photo) {
    const height = Math.max(MIN_PHOTO_HEIGHT, photo.getBoundingClientRect().height - overflow);
    dialog.style.setProperty('--tps-photo-height', `${height}px`);
  }
}

/**
 * Sets the window up for an add from the buy card or for fixing a bag line: the main button's label and colour,
 * and the summary's price and quantity (both hidden in edit mode). The price is display text only, never parsed.
 * @param {HTMLElement} modalComponent
 * @param {'add' | 'edit'} mode
 * @param {{ price?: unknown, quantity?: number }} [add] - The add event's price and the pending quantity.
 */
function setMode(modalComponent, mode, add = {}) {
  const saveButton = modalComponent.querySelector('[data-treatment-param-save]');
  if (saveButton instanceof HTMLElement) {
    const label = mode === 'add' ? saveButton.dataset.labelAdd : saveButton.dataset.labelSave;
    if (label) saveButton.textContent = label;
  }

  const price = modalComponent.querySelector('[data-moment-summary-price]');
  if (price instanceof HTMLElement) {
    if (!liquidPrices.has(price)) liquidPrices.set(price, price.textContent?.trim() ?? '');
    const fromEvent = typeof add.price === 'string' ? add.price.trim() : '';
    price.textContent = fromEvent || liquidPrices.get(price);
    price.hidden = mode === 'edit' || !price.textContent;
  }

  const quantity = modalComponent.querySelector('[data-moment-summary-qty]');
  if (quantity instanceof HTMLElement) {
    const count = mode === 'add' ? Number(add.quantity) || 1 : 1;
    quantity.textContent = count > 1 ? `× ${count}` : '';
    quantity.hidden = count <= 1;
  }

  const dialog = /** @type {any} */ (modalComponent).refs?.dialog ?? modalComponent.querySelector('dialog');
  if (!(dialog instanceof HTMLElement)) return;
  // The opener is the buy card holding focus (its Add to bag), else the page's first one.
  const buyCard = mode === 'add' ? (document.activeElement?.closest('[data-moment-buy]') ?? document.querySelector('[data-moment-buy]')) : null;
  copyAccent(dialog, buyCard);
  const card = buyCard?.querySelector('[data-moment-buy-card]') ?? null;
  fitFromSizer = !card;
  fitCard = card ?? modalComponent.querySelector('[data-treatment-param-sizer-card]');
}

/**
 * @returns {Set<string>} Section ids used by cart item components.
 */
function getCartSectionIds() {
  const sections = new Set();

  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sections.add(item.dataset.sectionId);
    }
  }

  return sections;
}

/**
 * Preserves existing properties while updating treatment values.
 * @param {Record<string, string> | null | undefined} currentProperties
 * @param {string} designStyle
 * @param {string} shade
 */
function buildProperties(currentProperties, designStyle, shade) {
  const properties = { ...(currentProperties || {}) };
  properties['Design Style'] = designStyle;
  properties['Shade'] = shade;
  return properties;
}

/**
 * Opens the bag drawer after a successful save, once the Style and Shade dialog has finished closing.
 * @param {HTMLElement} modalComponent
 * @param {Promise<void> | undefined} closing - The pending `closeDialog()` promise.
 */
async function openCartDrawerAfterSave(modalComponent, closing) {
  if (typeof Theme !== 'undefined' && Theme.template?.name === 'cart') return;
  if (!document.querySelector('cart-drawer-component')) return;

  await Promise.race([closing, new Promise((resolve) => setTimeout(resolve, CLOSE_FALLBACK_MS))]);
  await customElements.whenDefined('cart-drawer-component');

  // Still closing after the fallback: opening now would fight its scroll lock, so skip.
  if (/** @type {any} */ (modalComponent).refs?.dialog?.open) return;

  const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
  if (!drawer || drawer.refs?.dialog?.open) return;

  drawer.open();
}

/**
 * Adds a new Moment line that already carries the chosen Design Style and Shade.
 * @param {{ id: number, quantity: number }} item
 * @param {string} designStyle
 * @param {string} shade
 * @param {string} sections - Comma-separated section ids to re-render.
 * @returns {Promise<any>} The updated cart, with the re-rendered sections attached.
 */
async function addMomentLine(item, designStyle, shade, sections) {
  const body = JSON.stringify({
    items: [{ id: item.id, quantity: item.quantity, properties: buildProperties(null, designStyle, shade) }],
    sections,
    sections_url: window.location.pathname,
  });

  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
  const added = await response.json();

  if (!response.ok || added?.status) {
    throw new Error(added?.description || added?.message || 'Unable to add Moment to your bag. Please try again.');
  }

  // add.js returns only the new line, so read the whole cart for the item count and line list.
  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();
  return { ...cart, sections: added.sections };
}

/**
 * Writes the chosen Design Style and Shade onto an existing Moment cart line.
 * @param {number} line - 1-based cart line index.
 * @param {string} designStyle
 * @param {string} shade
 * @param {string} sections - Comma-separated section ids to re-render.
 * @returns {Promise<any>} The updated cart, with the re-rendered sections attached.
 */
async function updateMomentLine(line, designStyle, shade, sections) {
  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();
  const lineItem = cart?.items?.[line - 1];

  if (!lineItem || !lineItem.product_title?.includes('Moment by Neer')) {
    throw new Error('Target cart item was not found.');
  }

  const body = JSON.stringify({
    line,
    quantity: lineItem.quantity,
    properties: buildProperties(lineItem.properties, designStyle, shade),
    sections,
    sections_url: window.location.pathname,
  });

  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const updatedCart = await response.json();

  // change.js reports some failures (e.g. 400 bad_request) without an `errors` key.
  if (!response.ok || updatedCart?.errors) {
    throw new Error(updatedCart?.errors || updatedCart?.description || 'Unable to save your selection. Please try again.');
  }

  return updatedCart;
}

function initTreatmentParamSelector() {
  const modalComponent = document.getElementById(MODAL_ID);
  if (!(modalComponent instanceof HTMLElement)) return;

  const form = modalComponent.querySelector(FORM_SELECTOR);
  if (!(form instanceof HTMLElement)) return;

  const lineInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="line"]'));
  if (!(lineInput instanceof HTMLInputElement)) return;

  const designStyleInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
    form.querySelectorAll('input[name="designStyle"]')
  );
  const shadeInputs = /** @type {NodeListOf<HTMLInputElement>} */ (form.querySelectorAll('input[name="shade"]'));
  const errorElement = /** @type {HTMLElement | null} */ (modalComponent.querySelector('[data-treatment-param-error]'));
  const saveButton = modalComponent.querySelector('[data-treatment-param-save]');

  if (!(saveButton instanceof HTMLButtonElement)) return;

  const resetSelection = () => {
    setError(errorElement, '');

    for (const input of designStyleInputs) {
      input.checked = false;
    }

    for (const input of shadeInputs) {
      input.checked = false;
    }

    markMissing(modalComponent, 'designStyle', false);
    markMissing(modalComponent, 'shade', false);
  };

  // A pick clears its own step's "choose one" hint.
  form.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && (target.name === 'designStyle' || target.name === 'shade')) {
      markMissing(modalComponent, target.name, false);
    }
  });

  // "Add to bag" on the Moment card: nothing is in the cart until Save.
  modalComponent.addEventListener(ADD_REQUEST_EVENT, (event) => {
    // Already open (a second Add to bag press): keep the shopper's picks.
    if (/** @type {any} */ (modalComponent).refs?.dialog?.open) return;

    const detail = /** @type {CustomEvent} */ (event).detail || {};
    pendingAdd = { id: Number(detail.id), quantity: Number(detail.quantity) || 1 };
    lineInput.value = '';
    resetSelection();
    setMode(modalComponent, 'add', { price: detail.price, quantity: pendingAdd.quantity });
    /** @type {any} */ (modalComponent).showDialog?.();
  });
  modalComponent.dataset.addReady = 'true';

  // The dialog opens a frame after showDialog(), once the page is pinned in place: measure the card then, and again
  // on resize (a rotated phone) while it stays open.
  const dialog = /** @type {any} */ (modalComponent).refs?.dialog ?? modalComponent.querySelector('dialog');
  modalComponent.addEventListener(DialogOpenEvent.eventName, () => fitToCard(dialog, fitCard, fitFromSizer));
  window.addEventListener('resize', () => {
    if (dialog?.open) fitToCard(dialog, fitCard, fitFromSizer);
  });

  saveButton.addEventListener('click', async () => {
    if (!pendingAdd && !lineInput.value) {
      setError(errorElement, 'Unable to identify this cart line item. Please try again.');
      return;
    }

    const selectedDesignStyle = form.querySelector('input[name="designStyle"]:checked');
    const selectedShade = form.querySelector('input[name="shade"]:checked');

    const designStyle = selectedDesignStyle instanceof HTMLInputElement ? selectedDesignStyle.value : '';
    const shade = selectedShade instanceof HTMLInputElement ? selectedShade.value : '';

    if (!designStyle || !shade) {
      markMissing(modalComponent, 'designStyle', !designStyle);
      markMissing(modalComponent, 'shade', !shade);
      // Focus lands on the first step still missing; the whole step scrolls into view, since its radio is 1 px (W6).
      const firstMissing = form.querySelector(`input[name="${designStyle ? 'shade' : 'designStyle'}"]`);
      if (firstMissing instanceof HTMLInputElement) {
        firstMissing.focus({ preventScroll: true });
        firstMissing.closest('fieldset')?.scrollIntoView?.({ block: 'nearest' });
      }
      return;
    }

    saveButton.disabled = true;

    setError(errorElement, '');

    try {
      const sections = Array.from(getCartSectionIds()).join(',');
      const updatedCart = pendingAdd
        ? await addMomentLine(pendingAdd, designStyle, shade, sections)
        : await updateMomentLine(Number(lineInput.value), designStyle, shade, sections);

      pendingAdd = null;

      const closing = /** @type {any} */ (modalComponent).closeDialog?.();

      modalComponent.dispatchEvent(
        new CartUpdateEvent(updatedCart, MODAL_ID, {
          source: 'treatment-param-selector',
          sections: updatedCart.sections,
          itemCount: updatedCart.item_count,
        })
      );

      void openCartDrawerAfterSave(modalComponent, closing);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save your selection. Please try again.';
      setError(errorElement, message);
    } finally {
      saveButton.disabled = false;
    }
  });
}

/**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
function handleCartUpdate(event) {
  if (!(event instanceof Event)) return;
  const { detail } = event;
  if (!detail) return;
  // A Save from this dialog shouldn't reopen it for some other unfinished Moment line.
  if (detail.data?.source === 'treatment-param-selector') return;
  const resource = /** @type {any} */ detail.resource;
  const items = resource?.items || [];
  const index = items.findIndex((item) => /Moment by Neer/i.test(item.title));
  const momentItem = index !== -1 ? items[index] : null;
  if (!momentItem) return;
  if (momentItem.properties?.['Design Style'] && momentItem.properties?.['Shade']) return;
  
  const modal = /** @type {any} */ (document.getElementById(MODAL_ID));
  const form = modal.querySelector(FORM_SELECTOR);
  if (!(form instanceof HTMLElement)) return;

  const lineInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="line"]'));
  if (!(lineInput instanceof HTMLInputElement)) return;
  
  pendingAdd = null;
  lineInput.value = String(index + 1);
  setMode(modal, 'edit');

  if (modal) {
    modal.showDialog?.();
  }
}


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTreatmentParamSelector);
} else {
  initTreatmentParamSelector();
}
document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

